import { useEffect, useMemo, useRef, useState } from "react";
import {
  Typography,
  Box,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { activateClientRequest } from "../../../../../../utils/APICalls";
import Loading from "../../../../../../components/loading/loading";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../../../components/table/table";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import TableHeaderTextField from "../../../../../../components/table-header-text-filed/table-header-text-filed";
import TableHeaderSelectField from "../../../../../../components/table-header-select-field/table-header-select-field";
import TableHeaderDatePicker from "../../../../../../components/table-header-datepicker/table-header-datepicker";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import dayjs from "dayjs";
import {
  getClientDetailsRequest,
  getClientUsersRequest,
} from "../../../../../../redux/clients/slice";
import CloseIcon from "@mui/icons-material/Close";

const StatusRenderer = ({ rowData }: any) => {
  return (
    <Box sx={{ height: 52, display: "flex", alignItems: "center" }}>
      {rowData?.isActive ? (
        <Chip
          color="success"
          icon={<CheckOutlinedIcon />}
          label="Active"
          variant="outlined"
        />
      ) : (
        <Chip
          color="error"
          icon={<CloseOutlinedIcon />}
          label="Inactive"
          variant="outlined"
        />
      )}
    </Box>
  );
};

export default function ReActivateClientDialog({
  showDialog,
  closeDialog,
  id,
}: any) {
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [rowData, setRowData] = useState<any>([]);
  const [filterParams, setFilterParams] = useState({});
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const { users } = useSelector((state: any) => state.clients);

  const rowCountRef = useRef(users?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (users?.totalCount !== undefined) {
      rowCountRef.current = users?.totalCount;
    }
    return rowCountRef.current;
  }, [users?.totalCount]);

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
  }, []);

  useEffect(() => {
    setRowData(users?.items);
  }, [users]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  const column: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      headerClassName: "hideRightSeparator",
      sortable: false,
      valueGetter: (value, row) => {
        return `${row.firstName || ""} ${row.lastName || ""}`;
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="name"
          sortField="name"
          headerName={params.colDef.headerName}
          width={params.colDef.width}
          showFilter={true}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "username",
      headerName: "Username",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="username"
          sortField="username"
          headerName={params.colDef.headerName}
          width={params.colDef.width}
          showFilter={true}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "primaryEmail",
      headerName: "Primary Email",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="primaryEmail"
          sortField="email"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={true}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "primaryPhone",
      headerName: "Primary Phone",
      width: 180,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="primaryPhone"
          sortField="phone"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={true}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <StatusRenderer rowData={params?.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="isActive"
          sortField="status"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={true}
          values={["Active", "Inactive"]}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "roleNames",
      headerName: "Role(s)",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="roleName"
          sortField="role"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={true}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "lastLoginDateTime",
      headerName: "Last Login",
      width: 200,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY") : " ",
      renderHeader: (params) => (
        <TableHeaderDatePicker
          field="lastLogin"
          sortField="lastLogin"
          headerName={params.colDef.headerName}
          width={params.colDef.width}
          showFilter={true}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
  ];

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      filters.id = id;
      dispatch(getClientUsersRequest(filters));
    }
  }, [filterParams]); // eslint-disable-line

  const onSubmitClick = () => {
    const payload = {
      id: id,
      userIds: rowSelectionModel,
    };
    activateClient(payload);
  };

  const activateClient = async (payload: any) => {
    setLoading(true);
    const response = await activateClientRequest(payload);
    setLoading(false);
    if (response?.status === 204) {
      dispatch(getClientDetailsRequest({ id }));
      closeDialog();
    }
  };

  return (
    <Dialog
      open={showDialog}
      onClose={() => closeDialog()}
      scroll={"paper"}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      fullWidth={true}
      maxWidth={"lg"}
    >
      <DialogTitle className="header-txt" id="scroll-dialog-title">
        <Typography
          sx={{ fontWeight: "540", letterSpacing: "0.6px", fontSize: 20 }}
        >
          Re-Activate Client?
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={closeDialog}
        sx={(theme) => ({
          position: "absolute",
          right: 12,
          top: 12,
          color: "rgba(0, 0, 0, 0.54)",
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <>
          <Typography>
            Select the users you would like to re-activate. Be advised that if
            you do not re-activate the previous account manager, one of the
            activated users or a new user will need to be designated as account
            manager.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Table
              rowData={rowData}
              columnDef={column}
              onRowSelectionModelChange={(newRowSelectionModel: any) => {
                setRowSelectionModel(newRowSelectionModel);
              }}
              rowSelectionModel={rowSelectionModel}
              disableColumnMenu
              headerHeight={104}
              rowCount={rowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
          </Box>
        </>
      </DialogContent>
      <DialogActions sx={{ mb: 0.5 }}>
        <Button onClick={closeDialog}>
          <Typography
            sx={{ fontWeight: "520", letterSpacing: "0.5px", fontSize: 14 }}
          >
            Cancel
          </Typography>
        </Button>
        <Button
          variant="contained"
          sx={{ ml: 2, mr: 1 }}
          onClick={onSubmitClick}
          autoFocus
          disabled={!(rowSelectionModel && rowSelectionModel.length > 0)}
        >
          <Typography
            sx={{ fontWeight: "520", letterSpacing: "0.5px", fontSize: 14 }}
          >
            Re-Activate Client
          </Typography>
        </Button>
      </DialogActions>
      {loading && <Loading />}
    </Dialog>
  );
}
